import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  Typography
} from '@mui/material';
import { ProjectStage } from '../../types';

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (project: any) => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    elevatorPitch: '',
    stage: 'Idea' as ProjectStage,
    industry: ''
  });
  const [error, setError] = useState('');

  const stages: ProjectStage[] = ['Idea', 'Validación', 'Crecimiento Temprano', 'Escalamiento'];
  
  const industries = [
    'Tecnología',
    'Salud Digital',
    'EdTech',
    'FinTech',
    'AgTech',
    'E-commerce',
    'Sostenibilidad',
    'Logística',
    'Entretenimiento',
    'Otro'
  ];

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError('');
  };

  const handleSubmit = () => {
    // Validaciones
    if (!formData.name.trim()) {
      setError('El nombre del proyecto es obligatorio');
      return;
    }
    if (formData.name.trim().length < 3) {
      setError('El nombre debe tener al menos 3 caracteres');
      return;
    }
    if (!formData.description.trim()) {
      setError('La descripción es obligatoria');
      return;
    }
    if (formData.description.trim().length < 20) {
      setError('La descripción debe tener al menos 20 caracteres');
      return;
    }
    if (!formData.industry) {
      setError('Debes seleccionar una industria');
      return;
    }

    // Crear proyecto
    const newProject = {
      id: `p_${Date.now()}`,
      name: formData.name,
      description: formData.description,
      elevatorPitch: formData.elevatorPitch || formData.description,
      stage: formData.stage,
      industry: formData.industry,
      ownerId: 'user_emprendedor_1', // Usuario actual
      documents: [],
      milestones: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSubmit(newProject);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      elevatorPitch: '',
      stage: 'Idea',
      industry: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Crear Nuevo Proyecto</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          
          <Alert severity="info">
            <Typography variant="body2">
              Completa la información básica de tu proyecto. Podrás agregar más detalles después.
            </Typography>
          </Alert>

          <TextField
            label="Nombre del Proyecto"
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            required
            placeholder="Ej: EcoTech Solutions"
            helperText="Un nombre claro y memorable para tu proyecto"
          />

          <TextField
            label="Descripción"
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            required
            multiline
            rows={3}
            placeholder="Describe tu proyecto en pocas palabras..."
            helperText={`${formData.description.length} caracteres (mínimo 20)`}
          />

          <TextField
            label="Elevator Pitch (Opcional)"
            value={formData.elevatorPitch}
            onChange={handleChange('elevatorPitch')}
            fullWidth
            multiline
            rows={2}
            placeholder="Resume tu propuesta de valor en una frase..."
            helperText="Una descripción breve y convincente de tu proyecto"
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              select
              label="Etapa Actual"
              value={formData.stage}
              onChange={handleChange('stage')}
              fullWidth
              required
            >
              {stages.map(stage => (
                <MenuItem key={stage} value={stage}>
                  {stage}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Industria"
              value={formData.industry}
              onChange={handleChange('industry')}
              fullWidth
              required
            >
              {industries.map(industry => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Alert severity="success">
            <Typography variant="body2">
              <strong>Próximos pasos:</strong> Después de crear tu proyecto, podrás agregar 
              documentos, definir hitos y solicitar mentoría.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          Crear Proyecto
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectModal;
